import { useFormContext } from "react-hook-form";

import { InputField } from "@ps-design-system/components/Input";
import {
  RadioGroupField,
  RadioGroupItem,
} from "@ps-design-system/components/RadioGroup";

import { useIntl } from "react-intl-compat";
import { getCurrencySymbol } from "~/i18n/i18n.utils";

import type { ListingUseType } from "../../../../../../components/src/services/listings.service";
import { USE_TYPE_SLUG } from "../../ActivateListingsPage.consts";
import { ActivateListingFormValues } from "../ActivateListingForm.schema";

type CleaningFeeFieldsProps = {
  spaceCountry: string;
  useType: ListingUseType;
};

/** The cleaning fee radio options, plus the flat fee input shown when "Additional flat fee" is selected. */
const CleaningFeeFields = ({
  spaceCountry,
  useType,
}: CleaningFeeFieldsProps) => {
  const intl = useIntl();
  const slug = USE_TYPE_SLUG[useType];

  const form = useFormContext<ActivateListingFormValues>();

  return (
    <>
      <RadioGroupField
        name="cleaningFeeOption"
        label={intl.formatMessage({
          id: "hosting.activate_listings.cleaning_fee_label",
          defaultMessage: "Cleaning fee",
        })}
        subcopy={intl.formatMessage({
          id: "hosting.activate_listings.cleaning_fee_subcopy",
          defaultMessage:
            "You are responsible for cleaning after each booking. You can charge a fee to cover related costs.",
        })}
        required
        testId={`activate-listing-form-${slug}-cleaning-fee`}
      >
        <RadioGroupItem
          value="INCLUDED"
          id="cleaningFeeIncluded"
          label={intl.formatMessage({
            id: "hosting.activate_listings.cleaning_fee_included",
            defaultMessage: "Included in hourly rate",
          })}
          data-testid={`activate-listing-form-${slug}-cleaning-fee-included-radio`}
        />
        <RadioGroupItem
          value="FLAT_RATE"
          id="cleaningFeeAdditional"
          label={intl.formatMessage({
            id: "hosting.activate_listings.cleaning_fee_additional",
            defaultMessage: "Additional flat fee",
          })}
          data-testid={`activate-listing-form-${slug}-cleaning-fee-additional-radio`}
        />
      </RadioGroupField>

      {form.watch("cleaningFeeOption") === "FLAT_RATE" && (
        <InputField
          fullWidth
          type="number"
          name="cleaningFeeValue"
          prefix={getCurrencySymbol(spaceCountry)}
          required
          placeholder="75"
          testId={`activate-listing-form-${slug}-cleaning-fee-value-input`}
        />
      )}
    </>
  );
};

export { CleaningFeeFields };
